import { useTranslations } from 'next-intl';
import { Icon } from '@/components/entities/Sprite';
import openModal from '@/modals/utils/openModal';
import { cn } from '@/utils/cn';
import { getDailyBonusUpToByIndex } from '../utils/dailyBonusUpToAmounts.utils';
import '../styles/dailyBonusItem.css';

export type DailyBonusCardState = 'ready' | 'claimed' | 'locked';

interface Props {
  type?: 'daily-bonus' | 'bundle-bonus';
  index?: number;
  rewardAmount: number;
  bonusDays: number;
  state: DailyBonusCardState;
  redirectToShop?: boolean;
  onClick?: () => void;
  className?: string;
  amountClassName?: string;
  iconClassName?: string;
}

const stateIconByType: Record<Exclude<DailyBonusCardState, 'ready'>, 'daily-bonus-clock' | 'daily-bonus-shop'> = {
  claimed: 'daily-bonus-clock',
  locked: 'daily-bonus-shop',
};

const stateRingByType: Record<DailyBonusCardState, string> = {
  ready: 'rotating-border shadow-[1px_2px_6px_0_#BD4DF9]',
  claimed: 'shadow-[1px_1px_8px_0_#BD4DF9]',
  locked: '',
};

const DailyBonusCard = ({ type, index, rewardAmount, bonusDays, state, redirectToShop = false, onClick, className, amountClassName, iconClassName }: Props) => {
  const t = useTranslations('daily-bonus');
  const isReady = state === 'ready';
  const isLocked = state === 'locked';
  const isClaimed = state === 'claimed';
  const isDailyBonus = type === 'daily-bonus';
  const isClaimAvailable = isReady && typeof onClick === 'function';

  const isShopCard = isDailyBonus && isLocked;
  const dailyCounter = Math.min(7, Math.max(1, bonusDays));
  const rewardUpToByIndex
    = typeof index === 'number' ? getDailyBonusUpToByIndex(index, rewardAmount) : rewardAmount;

  const handleCardClick = () => {
    if (isShopCard || !isReady || redirectToShop) {
      openModal('deposit');
      return;
    }

    if (!isClaimAvailable) {
      return;
    }

    onClick?.();
  };

  return (
    <button
      type="button"
      onClick={handleCardClick}
      className={cn(
        'relative isolate overflow-visible flex flex-col text-left rounded-[0.5rem] bg-c950 px-1 md:px-[0.0313rem] pb-2 pt-1 md:pb-1.5 md:pt-0.25 3xl:p-1 h-16.5 md:w-10.25 md:h-13 3xl:h-13.5 3xl:w-11.5 duration-300 transition-[color,background-color,box-shadow] md:hover:shadow-[1px_1px_10px_0_#FC52FF]',
        isShopCard ? '' : stateRingByType[state],
        !isShopCard && isReady ? 'overflow-hidden' : 'overflow-visible',
        className,
      )}
    >
      {isShopCard
        ? (
            <div className="absolute inset-0 z-1 flex flex-col items-center justify-center gap-1.25 md:gap-0 md:pb-1.25">
              <Icon name="daily-bonus-bag" className={cn('size-6 md:size-4 3xl:size-5 text-c300 mt-1  3xl:mt-1', iconClassName)} />
              <span className="font-medium text-[0.5625rem] leading-4 text-c300">{t('to-shop')}</span>
            </div>
          )
        : (
            <>
              {isReady && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="absolute inset-0 z-0 rotating-border overflow-hidden p-1.5 rounded-[0.5rem]" />
                  <div className="absolute inset-0.5 z-0 rounded-[0.5rem] bg-c950" />
                </div>
              )}

              {!isReady && (
                <span className={cn('absolute z-1 -top-0.5 -left-0.5 flex items-center transform-gpu backface-hidden', iconClassName)}>
                  <Icon name={stateIconByType[state]} className="size-6 md:size-5" />
                </span>
              )}
              {!isLocked && (
                <span className={cn('relative z-1 text-[0.625rem] pr-0.75 md:pr-1 3xl:pr-0.5 font-medium leading-4 w-full text-right', isClaimed ? 'text-c300 pr-0.5' : 'text-white')}>
                  {isDailyBonus ? `${dailyCounter}` : `${bonusDays}`}
                </span>
              )}
              <div className={cn('flex flex-col mt-auto mx-auto', (isLocked && isReady) && 'lg:mt-4.5')}>
                <span className="relative z-1 font-medium text-[0.5625rem] md:leading-4 leading-2 text-c300 flex justify-center items-center 3xl:-mb-1">{t('up-to')}</span>
                <div className={cn('relative z-1 font-medium text-sm leading-5 md:text-[0.6875rem] md:leading-[100%] md:font-semibold flex justify-center items-center 3xl:text-xs 3xl:leading-4 3xl:font-medium ', isClaimed ? 'text-c300' : 'text-white', amountClassName)}>
                  {rewardUpToByIndex}
                  {' '}
                  SC
                </div>
              </div>
            </>
          )}
    </button>
  );
};

export default DailyBonusCard;
